import { useRouter } from 'next/router';
import { useMemo } from 'react';

/**
 * Custom hook to get the search parameters from the current URL in a Next.js application.
 *
 * @returns {URLSearchParams} - The search parameters of the current URL.
 *
 * Example usage:
 * const searchParams = useNextSearchParams();
 */
export function useNextSearchParams(): URLSearchParams {
  // Get the current query from the Next.js router
  const { query } = useRouter();

  /**
   * Memoize the computation of the search parameters to optimize performance.
   * It creates a new URLSearchParams object using the current query.
   *
   * @returns {URLSearchParams} - The search parameters of the current URL.
   */
  const searchParams: URLSearchParams = useMemo(() => {
    return new URLSearchParams(query as Record<string, string>);
  }, [query]); // Dependency array to recompute the search parameters when query changes

  // Return the computed search parameters
  return searchParams;
}
